import React, { useState, useRef, useEffect } from 'react';
import { Music, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import '../styles/MusicPlayer.css';

const MusicPlayer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.35);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Click outside closes the controls
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (playerRef.current && !playerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowVolumeSlider(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.warn('Audio play request interrupted or blocked:', err);
            setIsPlaying(false);
          });
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleControls = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setShowVolumeSlider(false);
    }
  };

  return (
    <div className="music-player" ref={playerRef}>
      <audio 
        ref={audioRef} 
        src="/music/song.mp3" 
        loop
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Orijinal kontroller kapsülü (başta gizli, notaya basınca açılır) */}
      <div className={`music-controls ${isOpen ? 'controls-open' : 'controls-hidden'}`}>
        <button 
          onClick={togglePlay} 
          className="music-button"
          aria-label={isPlaying ? 'Duraklat' : 'Şarkıyı Çal'}
          title={isPlaying ? 'Durdur' : 'Şarkıyı Çal 💕'}
        >
          {isPlaying ? (
            <Pause className="music-icon" />
          ) : (
            <Play className="music-icon" />
          )}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowVolumeSlider(!showVolumeSlider);
          }}
          className="volume-button"
          aria-label="Ses seviyesi"
          title="Ses Seviyesi"
        >
          {volume === 0 ? (
            <VolumeX className="music-icon" />
          ) : (
            <Volume2 className="music-icon" />
          )}
        </button>

        {showVolumeSlider && (
          <div className="volume-slider-container fade-in" onClick={(e) => e.stopPropagation()}>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
            <span className="volume-label">{Math.round(volume * 100)}%</span>
          </div>
        )}
      </div>

      {/* Kutusu olmayan, önceki gibi serbest uçan parlak müzik notası */}
      <button
        type="button"
        onClick={toggleControls}
        className="music-note-trigger"
        aria-label="Müzik Çalar"
        title={isPlaying ? 'Şarkı Çalıyor 💕 (Kontrolleri Aç/Kapat)' : 'Müzik Çalar 💕 (Açmak için tıkla)'}
      >
        <Music className={`music-note ${isPlaying ? 'music-playing' : ''}`} />
      </button>
    </div>
  );
};

export default MusicPlayer;
